import { SpotlightItem } from '@thoughtspot/global-nav';
import { useCallback, useMemo, useState } from 'react';
import { useSpotlightAnswerItems } from './use-spotlight-answer-items';
import { useSpotlightCollectionItems } from './use-spotlight-collection-items';
import { useSpotlightCreateItems } from './use-spotlight-create-items';
import { useSpotlightLiveboardItems } from './use-spotlight-liveboard-items';
import { useSpotlightModelItems } from './use-spotlight-model-items';
import { useSpotlightTableItems } from './use-spotlight-table-items';

interface FilterConfig {
    match: (id: string) => boolean;
    activate?: () => void;
    items: SpotlightItem[];
    loading: boolean;
}

export function useSpotlightFilter() {
    const {
        liveboardItems,
        liveboardItemsLoading,
        activate: activateLiveboards,
    } = useSpotlightLiveboardItems();
    const {
        answerItems,
        answerItemsLoading,
        activate: activateAnswers,
    } = useSpotlightAnswerItems();
    const createItems = useSpotlightCreateItems();
    const {
        collectionItems,
        collectionItemsLoading,
        activate: activateCollections,
    } = useSpotlightCollectionItems();
    const {
        modelItems,
        modelItemsLoading,
        activate: activateModels,
    } = useSpotlightModelItems();
    const {
        tableItems,
        tableItemsLoading,
        activate: activateTables,
    } = useSpotlightTableItems();

    const [activeFilterId, setActiveFilterId] = useState<string | null>(null);

    const filterConfigs = useMemo<FilterConfig[]>(
        () => [
            {
                match: id =>
                    id.includes('liveboard') || id.includes('pinboard'),
                activate: activateLiveboards,
                items: liveboardItems,
                loading: liveboardItemsLoading,
            },
            {
                match: id => id.includes('answer'),
                activate: activateAnswers,
                items: answerItems,
                loading: answerItemsLoading,
            },
            {
                match: id => id === 'slash-create',
                items: createItems,
                loading: false,
            },
            {
                match: id => id.includes('collection'),
                activate: activateCollections,
                items: collectionItems,
                loading: collectionItemsLoading,
            },
            {
                match: id => id.includes('model'),
                activate: activateModels,
                items: modelItems,
                loading: modelItemsLoading,
            },
            {
                match: id => id.includes('table'),
                activate: activateTables,
                items: tableItems,
                loading: tableItemsLoading,
            },
        ],
        [
            activateLiveboards,
            liveboardItems,
            liveboardItemsLoading,
            activateAnswers,
            answerItems,
            answerItemsLoading,
            createItems,
            activateCollections,
            collectionItems,
            collectionItemsLoading,
            activateModels,
            modelItems,
            modelItemsLoading,
            activateTables,
            tableItems,
            tableItemsLoading,
        ],
    );

    const handleFilterSelect = useCallback(
        (filter: { id: string }) => {
            setActiveFilterId(filter.id);
            filterConfigs.find(c => c.match(filter.id))?.activate?.();
        },
        [filterConfigs],
    );

    const { filterItems, filterItemsLoading } = useMemo(() => {
        if (!activeFilterId)
            return { filterItems: [], filterItemsLoading: false };
        const config = filterConfigs.find(c => c.match(activeFilterId));
        return {
            filterItems: config?.items ?? [],
            filterItemsLoading: config?.loading ?? false,
        };
    }, [activeFilterId, filterConfigs]);

    return { filterItems, filterItemsLoading, handleFilterSelect };
}
